import { captureGenericError } from 'src/lib/sentry';
import { PetSpeciesID, PetState } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { Attributes, PetState as ReduxPet } from '../pet/pet.redux';
import * as Visit from 'src/replicant/visit';
import StateObserver from 'src/StateObserver';
import { petAnimationNames } from '../../replicant/pet/PetAssets';
import { ScriptID } from 'src/replicant/conversation/conservation.ruleset';
import { getScripts } from '../conversation/conversation.utils';

type ConversationState = { activeScript: ScriptID | undefined };
export type AppearanceParams = Pick<ConversationState, 'activeScript'> &
  Pick<ReduxPet, 'action'> &
  Pick<ReduxPet, 'species'> & {
    currentCareAction: boolean;
  } & ReduxPet['statusEffects'] &
  Attributes & { id: string };

export type AnimationName = Exclude<
  ReturnType<typeof getStatusAnimationName>,
  undefined
>;

export function getStatusAnimationName(status: PetState) {
  switch (status) {
    case 'dirty':
    case 'very_dirty':
      return 'dirty';

    case 'hungry':
    case 'very_hungry':
      return 'hungry';

    case 'lonely':
    case 'very_lonely':
      return 'lonely';

    case 'pee':
    case 'very_pee':
      return 'pee';

    case 'want_to_go_out':
      return 'want_out';

    case 'happy':
    case 'sick':
    case 'scared':
    case 'tooth_decay':
      return status;

    default:
      return undefined;
  }
}

export function getPetAnimation(
  { activeScript, action, species, ...params }: AppearanceParams,
  movement: 'idle' | 'walking',
) {
  const speciesAnimations = getSpeciesAnimations(species!);
  const script = activeScript ? getScripts()[activeScript] : null;

  const animationOverride =
    action ?? (movement === 'idle' && script?.animation);

  if (animationOverride) {
    let animation =
      speciesAnimations[animationOverride as keyof typeof speciesAnimations];
    if (!animation) {
      captureGenericError(
        `Animation not defined for species: "${species}", animation: "${animationOverride}"`,
        null,
      );
      animation = speciesAnimations.normal.idle;
    }
    return animation as string;
  }

  const { user } = StateObserver.getState();
  const isVisiting = Visit.getters.isVisiting(user);
  const petState: PetState =
    script?.expression ?? Pet.ruleset.getPetState(params, params, isVisiting);

  if (petState === 'sleeping') {
    captureGenericError(
      `petState sleeping should not be called for species ${species}, state ${petState}`,
      null,
    );

    return speciesAnimations['normal'].idle;
  }

  let movementAnimations = speciesAnimations[petState];

  if (!movementAnimations) {
    captureGenericError(
      `Animation not defined for species ${species}, state ${petState}`,
      null,
    );
    movementAnimations = speciesAnimations.normal;
  }

  return movementAnimations[movement];
}

export function getSpeciesAnimations(species: PetSpeciesID) {
  const speciesAnimations = petAnimationNames[species];
  if (speciesAnimations) {
    return speciesAnimations;
  }

  captureGenericError(`Animation not defined for species ${species}`, null);
  return petAnimationNames['dog'];
}
